import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  CameraAlt as CameraIcon,
  PhotoCamera as LensIcon,
  Extension as AccessoryIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { brandedProductName } from '../utils/brandIdentity';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { collectManagedImages } from '../utils/canonicalProductImages';
import {
  CardSkeleton,
  LoadingState,
  PageContainer,
  PageHeader,
  StatusChip,
  StatusTone,
  statusLabel,
} from '../components/ui';
import { AdminProductDetailDialog } from '../components/admin/productDetail/AdminProductDetailDialog';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { usePermalinkNavigation } from '../hooks/useProductPermalink';
import type { AdminCanonicalProduct } from '../components/admin/productDetail/productDetailTypes';

interface CanonicalProduct {
  id: string;
  sku: string;
  upc?: string;
  name: string;
  brand: string;
  model?: string;
  category: string;
  productType: string;
  pricing?: {
    msrp?: number;
    map?: number;
    retail?: number;
    wholesale?: number;
    salePrice?: number;
    salePriceSource?: 'scrape' | 'map_rebate' | 'manual';
    instantRebate?: number;
    tradeCredit?: number;
    rebateStartAt?: string | Date;
    rebateEndAt?: string | Date;
    currency?: string;
    priceAsOf?: any;
    priceLastChecked?: any;
    vendorPriceAsOf?: any;
    effectiveDate?: string | Date;
    expirationDate?: string | Date;
  };
  description?: string;
  images?: Array<string | { url: string; alt?: string; isPrimary?: boolean }>;
  specifications?: Record<string, any>;
  specificationsRaw?: Record<string, any>;
  status: string;
  source?: {
    vendorId?: string;
    connectorId?: string;
    sourceId?: string;
    rawData?: {
      hostedImages?: string[];
      [key: string]: unknown;
    };
  };
  createdAt?: string;
  updatedAt?: string;
}

interface ProductStats {
  total: number;
  byBrand: Record<string, number>;
  byCategory: Record<string, number>;
  byProductType: Record<string, number>;
  byStatus: Record<string, number>;
}

/** Raw category slugs ("memory-card") shown humanized ("Memory card"). Display only; filter values stay raw. */
const categoryLabel = (category?: string): string => (category ? statusLabel(category) : '-');

const AdminCanonicalProductsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Direct product links (MOT-833): the open product lives in the address, /app/admin/products/:productId.
  const { enabled: permalinksOn } = useOrgFeatureFlag('product_permalinks');
  const { productId: routeProductId = '' } = useParams<{ productId: string }>();
  const permalinkNav = usePermalinkNavigation('/app/admin/products', permalinksOn);
  const deepLinkedProductId = searchParams.get('productId') || '';
  const deepLinkedSku = searchParams.get('sku') || '';
  const [deepLinkError, setDeepLinkError] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<CanonicalProduct | null>(null);
  // Fetch product stats
  const { data: stats, isLoading: statsLoading } = useQuery<ProductStats>({
    queryKey: ['canonical-stats'],
    queryFn: async () => {
      const response = await makeApiCall('/normalization/canonical/stats', {
        headers: await getAuthHeaders(),
      });
      const data = await response.json();
      return data.data;
    },
  });

  // Fetch products
  const { data: productsData, isLoading: productsLoading, refetch } = useQuery({
    queryKey: ['canonical-products', page, rowsPerPage, brandFilter, categoryFilter, typeFilter, searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: rowsPerPage.toString(),
        offset: (page * rowsPerPage).toString(),
      });
      if (brandFilter) params.append('brand', brandFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (typeFilter) params.append('productType', typeFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await makeApiCall(`/normalization/canonical?${params}`, {
        headers: await getAuthHeaders(),
      });
      const json = await response.json();
      // API returns { success, data: products[], total, limit, offset, hasMore }
      return {
        products: json.data ?? [],
        total: json.total ?? 0,
      };
    },
  });

  const products: CanonicalProduct[] = productsData?.products ?? [];
  const productTypeOptions = React.useMemo(
    () => Array.from(new Set(products.map((p) => p.productType).filter(Boolean))).sort(),
    [products],
  );
  const totalProducts = productsData?.total ?? 0;

  const {
    data: deepLinkedProduct,
    isError: deepLinkByIdFailed,
    isFetched: deepLinkByIdFetched,
    isFetching: deepLinkByIdFetching,
  } = useQuery({
    queryKey: ['canonical-product-by-id', deepLinkedProductId, deepLinkedSku],
    queryFn: async () => {
      const qs = deepLinkedSku ? `?sku=${encodeURIComponent(deepLinkedSku)}` : '';
      const response = await makeApiCall(
        `/normalization/canonical/${encodeURIComponent(deepLinkedProductId)}${qs}`,
        { headers: await getAuthHeaders() }
      );
      if (!response.ok) {
        throw new Error('Failed to load product');
      }
      const payload = await response.json();
      return payload.data as CanonicalProduct;
    },
    enabled: !!deepLinkedProductId,
    retry: false,
  });

  const needsSkuFallback =
    !!deepLinkedSku &&
    (!deepLinkedProductId ||
      (deepLinkByIdFetched && !deepLinkByIdFetching && (deepLinkByIdFailed || !deepLinkedProduct)));

  const { data: deepLinkedBySku, isFetched: deepLinkBySkuFetched, isError: deepLinkBySkuFailed } =
    useQuery({
      queryKey: ['canonical-product-by-sku', deepLinkedSku],
      queryFn: async () => {
        const params = new URLSearchParams({
          search: deepLinkedSku,
          limit: '10',
          offset: '0',
        });
        const response = await makeApiCall(`/normalization/canonical?${params}`, {
          headers: await getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error('Failed to load product by SKU');
        }
        const payload = await response.json();
        const products = (payload.data ?? []) as CanonicalProduct[];
        const exact =
          products.find((p) => (p.sku || '').toLowerCase() === deepLinkedSku.toLowerCase()) ||
          products.find((p) => (p.model || '').toLowerCase() === deepLinkedSku.toLowerCase());
        return exact || products[0] || null;
      },
      enabled: needsSkuFallback,
      retry: false,
    });

  useEffect(() => {
    const product = deepLinkedProduct || deepLinkedBySku;
    if (!product) return;
    setSelectedProduct(product);
    setDeepLinkError('');
  }, [deepLinkedProduct, deepLinkedBySku]);

  // An older ?productId= / ?sku= link, once it resolves, moves to the product's own address. This runs
  // on the flag too, so a lookup that finishes before the flag is read still redirects when it arrives.
  useEffect(() => {
    const product = deepLinkedProduct || deepLinkedBySku;
    if (!permalinksOn || routeProductId || !product?.id) return;
    const rest = new URLSearchParams(searchParams);
    rest.delete('productId');
    rest.delete('sku');
    const search = rest.toString();
    navigate(
      { pathname: `/app/admin/products/${encodeURIComponent(product.id)}`, search: search ? `?${search}` : '' },
      { replace: true },
    );
  }, [permalinksOn, routeProductId, deepLinkedProduct, deepLinkedBySku, searchParams, navigate]);

  // A ?productId= link with no SKU needs no lookup at all: the id is the address.
  useEffect(() => {
    if (!permalinksOn || routeProductId || !deepLinkedProductId || deepLinkedSku) return;
    const rest = new URLSearchParams(searchParams);
    rest.delete('productId');
    const search = rest.toString();
    navigate(
      { pathname: `/app/admin/products/${encodeURIComponent(deepLinkedProductId)}`, search: search ? `?${search}` : '' },
      { replace: true },
    );
  }, [permalinksOn, routeProductId, deepLinkedProductId, deepLinkedSku, searchParams, navigate]);

  useEffect(() => {
    if (!deepLinkedProductId && !deepLinkedSku) {
      setDeepLinkError('');
      return;
    }
    const idDone = !deepLinkedProductId || (deepLinkByIdFetched && !deepLinkByIdFetching);
    const skuDone = !needsSkuFallback || deepLinkBySkuFetched;
    if (!idDone || !skuDone) return;
    if (deepLinkedProduct || deepLinkedBySku) {
      setDeepLinkError('');
      return;
    }
    if (deepLinkByIdFailed || deepLinkBySkuFailed || needsSkuFallback || deepLinkedProductId) {
      setDeepLinkError(
        `Could not open product${deepLinkedSku ? ` for SKU ${deepLinkedSku}` : ''}${
          deepLinkedProductId ? ` (${deepLinkedProductId})` : ''
        }. It may use a different catalog id — try searching by SKU.`
      );
    }
  }, [
    deepLinkedProductId,
    deepLinkedSku,
    deepLinkedProduct,
    deepLinkedBySku,
    deepLinkByIdFetched,
    deepLinkByIdFetching,
    deepLinkByIdFailed,
    deepLinkBySkuFetched,
    deepLinkBySkuFailed,
    needsSkuFallback,
  ]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getProductTypeIcon = (type: string) => {
    switch (type) {
      case 'camera':
        return <CameraIcon fontSize="small" />;
      case 'lens':
        return <LensIcon fontSize="small" />;
      default:
        return <AccessoryIcon fontSize="small" />;
    }
  };

  const getStatusTone = (status: string): StatusTone => {
    switch (status) {
      case 'active':
        return 'positive';
      case 'draft':
        return 'caution';
      case 'archived':
        return 'problem';
      default:
        return 'neutral';
    }
  };

  const formatPrice = (price?: number, currency = 'USD') => {
    if (!price) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(price);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const clearFilters = () => {
    setBrandFilter('');
    setCategoryFilter('');
    setTypeFilter('');
    setSearchTerm('');
    setPage(0);
  };

  const closeSelectedProductDialog = () => {
    setSelectedProduct(null);
    if (permalinksOn && routeProductId) {
      permalinkNav.close();
      return;
    }
    setDeepLinkError('');
    const next = new URLSearchParams(searchParams);
    let changed = false;
    for (const key of ['productId', 'sku']) {
      if (next.has(key)) {
        next.delete(key);
        changed = true;
      }
    }
    if (changed) {
      setSearchParams(next, { replace: true });
    }
  };

  const refreshCatalogLists = () => {
    queryClient.invalidateQueries({ queryKey: ['canonical-products'] });
    queryClient.invalidateQueries({ queryKey: ['canonical-stats'] });
  };

  return (
    <PageContainer>
      <PageHeader
        title="Canonical Products"
        description="Catalog admin: edit canonical product records, MSRP/MAP and status. For browsing, use Product Browser."
        actions={
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            variant="outlined"
          >
            Refresh
          </Button>
        }
      />

      {deepLinkError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={() => setDeepLinkError('')}
          action={
            deepLinkedSku ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setSearchTerm(deepLinkedSku);
                  setPage(0);
                  setDeepLinkError('');
                }}
              >
                Search SKU
              </Button>
            ) : undefined
          }
        >
          {deepLinkError}
        </Alert>
      )}

      {/* Stats Cards */}
      {statsLoading ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
              <CardSkeleton lines={1} />
            </Grid>
          ))}
        </Grid>
      ) : stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Total Products
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.total.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Brands
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {Object.keys(stats.byBrand || {}).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Categories
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {Object.keys(stats.byCategory || {}).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Active
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {(stats.byStatus?.active || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Brand</InputLabel>
              <Select
                value={brandFilter}
                label="Brand"
                onChange={(e) => {
                  setBrandFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All Brands</MenuItem>
                {Object.entries(stats?.byBrand || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([brand, count]) => (
                    <MenuItem key={brand} value={brand}>
                      {brand} ({count})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {Object.entries(stats?.byCategory || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, count]) => (
                    <MenuItem key={category} value={category}>
                      {categoryLabel(category)} ({count})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All Types</MenuItem>
                {Object.entries(stats?.byProductType || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count]) => (
                    <MenuItem key={type} value={type}>
                      {type} ({count})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Button
              startIcon={<FilterIcon />}
              onClick={clearFilters}
              disabled={!brandFilter && !categoryFilter && !typeFilter && !searchTerm}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Products Table */}
      <Paper>
        <TableContainer>
          {productsLoading ? (
            <LoadingState size="page" />
          ) : products.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              No products found. Try adjusting your filters or run normalization first.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">MSRP</TableCell>
                  <TableCell align="right">MAP</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow
                    key={product.id}
                    hover
                    onClick={() => {
                      setSelectedProduct(product);
                      permalinkNav.open(product.id);
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={collectManagedImages(product)[0]?.url}
                          variant="rounded"
                          sx={{ width: 40, height: 40 }}
                        >
                          {getProductTypeIcon(product.productType)}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            title={brandedProductName(product.brand, product.name)}
                            sx={{
                              maxWidth: 320,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                            }}
                          >
                            {brandedProductName(product.brand, product.name)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: {product.sku}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{product.brand}</TableCell>
                    <TableCell>
                      <Chip label={categoryLabel(product.category)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getProductTypeIcon(product.productType)}
                        label={product.productType}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {!product.pricing?.msrp && !!product.pricing?.map ? (
                        <Tooltip title="Missing MSRP: this product has a MAP value but no MSRP.">
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ cursor: 'help', textDecoration: 'underline dotted' }}
                          >
                            -
                          </Typography>
                        </Tooltip>
                      ) : (
                        formatPrice(product.pricing?.msrp, product.pricing?.currency)
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatPrice(product.pricing?.map, product.pricing?.currency)}
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={product.status}
                        size="small"
                        tone={getStatusTone(product.status)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedProduct(product);
                            permalinkNav.open(product.id);
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          component="div"
          count={totalProducts}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <AdminProductDetailDialog
        productId={permalinksOn ? routeProductId || null : selectedProduct?.id ?? null}
        initialProduct={
          (permalinksOn && selectedProduct?.id !== routeProductId ? null : selectedProduct) as AdminCanonicalProduct | null
        }
        permalink={permalinksOn}
        onClose={closeSelectedProductDialog}
        onChanged={refreshCatalogLists}
        onOpenProduct={(next) => {
          if (permalinksOn) {
            // A shared link has no list selection behind it: the address alone says what is open.
            if (next.id === routeProductId) return;
            setSelectedProduct(
              selectedProduct
                ? { ...selectedProduct, id: next.id, sku: next.sku || '', name: next.name || next.sku || selectedProduct.name }
                : null,
            );
            permalinkNav.open(next.id, { replace: true });
            return;
          }
          if (!selectedProduct || next.id === selectedProduct.id) return;
          setSelectedProduct({
            ...selectedProduct,
            id: next.id,
            sku: next.sku || '',
            name: next.name || next.sku || selectedProduct.name,
          });
        }}
        onDeleted={() => {
          if (selectedProduct?.id || routeProductId) {
            queryClient.removeQueries({ queryKey: ['admin-product-detail', selectedProduct?.id ?? routeProductId] });
          }
          closeSelectedProductDialog();
          refreshCatalogLists();
        }}
      />
    </PageContainer>
  );
};

export default AdminCanonicalProductsPage;
